<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { FillRenderer, useCanvasResponsiveWidth } from '@/features/FormBuilder';
  import { provideMatrixFillLab } from '../model/useMatrixFillLab';
  import CompilerOutputPanel from './matrix-fill/CompilerOutputPanel.vue';
  import DemoScriptPanel from './matrix-fill/DemoScriptPanel.vue';
  import ElementContractPanel from './matrix-fill/ElementContractPanel.vue';
  import FieldInspectorPanel from './matrix-fill/FieldInspectorPanel.vue';
  import LabHeader from './matrix-fill/LabHeader.vue';
  import MatrixInputPanel from './matrix-fill/MatrixInputPanel.vue';
  import ReportPanel from './matrix-fill/ReportPanel.vue';
  import ResponsesPanel from './matrix-fill/ResponsesPanel.vue';
  import UserFillPanel from './matrix-fill/UserFillPanel.vue';
  import UserReportPanel from './matrix-fill/UserReportPanel.vue';

  const route = useRoute();
  const router = useRouter();
  const lab = provideMatrixFillLab();
  const paperContainerRef = ref<HTMLElement | null>(null);
  const { displayWidth } = useCanvasResponsiveWidth(paperContainerRef);

  const routeId = computed(() => {
    const raw = route.params.id;
    const id = Number(Array.isArray(raw) ? raw[0] : raw);
    return Number.isInteger(id) && id > 0 ? id : null;
  });

  async function onSelectMatrix(id: number): Promise<void> {
    await router.replace({ name: 'MatrixFillPoc', params: { id } });
  }

  watch(
    routeId,
    async (id) => {
      if (id) await lab.load(id);
    },
    { immediate: true },
  );

  onMounted(() => Promise.all([lab.loadList(), lab.subjects.load()]));
</script>

<template>
  <div class="flex h-full min-h-0 flex-col gap-3 bg-[var(--color-white-02)] p-6">
    <header class="shrink-0">
      <h1 class="text-xl font-semibold text-[var(--color-gray-07)]">
        Laboratório do motor de preenchimento
      </h1>
      <p class="text-xs text-[var(--color-gray-05)]">
        A matriz salva pelo construtor (estrutura, regras e permissões) compilada em formulário, validação e relatório. Tudo à direita muda a folha na hora.
      </p>
    </header>

    <div class="flex min-h-0 flex-1 items-stretch gap-6">
      <div
        ref="paperContainerRef"
        class="min-w-0 flex-1 overflow-y-auto pb-6"
        data-testid="paper-scroller"
      >
        <p
          v-if="lab.error.value"
          class="text-sm text-red-600"
        >
          {{ lab.error.value }}
        </p>
        <p
          v-else-if="lab.isLoading.value"
          class="text-sm text-[var(--color-gray-05)]"
        >
          Carregando matriz...
        </p>
        <p
          v-else-if="!lab.compiled.value"
          class="text-sm text-[var(--color-gray-05)]"
        >
          Selecione uma matriz à direita.
        </p>
        <section
          v-else
          class="light-surface mx-auto rounded-md bg-white-01 p-8 text-gray-07 shadow-md"
          :style="{ width: `${displayWidth}px`, maxWidth: '100%' }"
          data-testid="fill-paper"
        >
          <FillRenderer
            :form="lab.compiled.value"
            :highlighted-field-id="lab.selectedFieldId.value"
            :extra-issues="lab.archiveAttempted.value ? lab.archive.value?.issues ?? [] : []"
            @update:value="lab.setValue"
            @update:cell="lab.setCellValue"
            @add-row="lab.addExtraRow"
            @remove-row="lab.removeExtraRow"
            @focus:field="lab.selectedFieldId.value = $event"
          />
        </section>
      </div>

      <aside
        class="flex w-[440px] shrink-0 flex-col gap-3 overflow-y-auto pb-2 pr-1 text-xs text-[var(--color-gray-07)]"
        data-testid="lab-aside"
      >
        <LabHeader
          :matrix-id="routeId"
          @select-matrix="onSelectMatrix"
        />

        <template v-if="lab.compiled.value">
          <DemoScriptPanel />
          <template v-if="lab.viewMode.value === 'user'">
            <UserFillPanel />
            <UserReportPanel />
          </template>
          <template v-else>
            <MatrixInputPanel />
            <CompilerOutputPanel />
            <FieldInspectorPanel />
            <ElementContractPanel />
            <ResponsesPanel />
            <ReportPanel />
          </template>
        </template>
      </aside>
    </div>
  </div>
</template>
